import {
    AfterViewChecked,
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChildren,
    ElementRef,
    Host,
    HostBinding,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    QueryList,
    Renderer2,
    SkipSelf,
    ViewChildren,
    ViewContainerRef,
    ViewEncapsulation
} from '@angular/core';
import _ from 'lodash';
import {ControlContainer} from '../../interfaces/ControlContainer';
import {AzoraSlot, SlotState} from '@azora/core';
import {untilDestroyed} from '@azora/common';
import {InputVariant} from "./input.model";

import {AzInputBase} from "./input.base";


@Component({
    selector: 'az-input',
    templateUrl: `./input.html`,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class AzoraInput extends AzInputBase implements OnInit, AfterViewInit, OnDestroy {
    
    @Input('placeholder')
    private _placeholder = ''
    
    get placeholder(): string {
        return (this._isFocused || !this.control.props.label) ? this.control.props.placeholder : ''
    }
    
    
    @Input()
    @HostBinding('attr.variant')
    variant: InputVariant = 'standard'
    
    @HostBinding('class.az-field') true
    
    
    @Input() range = [];
    
    
    get context(): object {
        return {
            $implicit: {
                value: this.control.value,
                dirty: this.control.dirty,
                valid: this.control.valid,
                invalid: this.control.invalid,
                props: this.control.props,
                
                passwordVisibility: this.passwordVisibility
            }
        }
    }
    
    
    templates = {
        password_toggler: null,
        prepend: null,
        append: null,
        before: null,
        after: null,
        clearable: null,
        feedback_icons: null
    };
    
    
    private get _inputElement(): HTMLInputElement {
        return this._elementRef.nativeElement.querySelector('input');
    }
    
    
    /**
     *  Template for AzInput content.
     * @docs-private
     */
    @ViewChildren(AzoraSlot)
    private _implicitContent: QueryList<AzoraSlot>;
    
    /**
     * Template provided inside the AzInput view that will be used if present
     * @docs-private
     */
    @ContentChildren(AzoraSlot)
    private _explicitContent: QueryList<AzoraSlot>;
    
    constructor(
            private _viewContainerRef: ViewContainerRef,
            private _changeDetectorRef: ChangeDetectorRef,
            private _elementRef: ElementRef,
            private _renderer: Renderer2,
            @Optional() @Host() @SkipSelf()  parent: ControlContainer
    ) {
        super(_elementRef, _changeDetectorRef);
        this.parent = parent;
    }
    
    ///-----------------------------------------------  Life Cycle Hook   -----------------------------------------------///
    
    ngOnInit(): void {
        super.ngOnInit();
        
        if (!this.root) {
            
            this.control.setControlState({
                value: this.value,
                props: {
                    label: this.label,
                    placeholder: this._placeholder,
                    type: this.type,
                    clearable: this.clearable
                },
                schema: this.schema
            });
        }
    }
    
    ngAfterViewInit(): void {
        super.ngAfterViewInit();
        
        _.each(this._implicitContent.toArray(), (content: AzoraSlot) => this._loadTemplate(content));
        
        _.each(this._explicitContent.toArray(), (content: AzoraSlot) => this._loadTemplate(content));
        
    }
    
    ngOnDestroy(): void {
    }
    
    
    private _loadTemplate = (slot: AzoraSlot) => {
        slot.contentRendered
                .pipe(untilDestroyed(this))
                .subscribe((state: SlotState) => {
                    this.templates[state.name] = state.template
                    this.cd.detectChanges();
                });
    };
    
    
    onChange = (e: Event) => {
        e.stopPropagation();
        e.preventDefault();
        
        
        let value = (<HTMLInputElement>e.target).value;
        const min = parseInt(this.range[0], 10);
        const max = parseInt(this.range[1], 10);
        if (this.range && this.type === 'number' && !(min <= parseInt(value, 10) && parseInt(value, 10) <= max)) {
            value = (<HTMLInputElement>e.target).value = this.range[1];
        }
        
        this.change.emit(value);
        
        this.control.setValue(value);
        
    };
    
    onFocus = (e: Event) => {
        e.stopPropagation();
        
        this.focus.emit();
        this.control.markAsFocused();
    };
    
    onBlur = (e: Event) => {
        e.stopPropagation();
        this.blur.emit();
        this.control.markAsBlurred();
    };
    
    onClear = () => {
        this.control.clear();
    };
    
    togglePasswordVisibility = () => {
        this.passwordVisibility = !this.passwordVisibility
    }
    
    
    _showAppendContent(): boolean {
        const portalNames = _.reduce(this.templates, (result: string[], value, key) => {
            if (value && ['append', 'feedback_icons'].includes(key)) result.push(key)
            return result
        }, [])
        
        return portalNames.length > 0
    }
    
    _showClearableContent(): boolean {
        return this.control.props.clearable && this.control.value
    }
    
    _showPasswordStrengthMeter(): boolean {
        return this.type === 'password' && this.control.value
    }
}

